/**
 * Photo Storage Service
 *
 * Handles uploading and retrieving photos from S3 via Lambda
 * Used for storing teaser photos for later HD analysis
 */

import { logError } from './errorLogger'

// API endpoint - use production API directly (no local Lambda needed)
const API_BASE = 'https://8plbeu8soh.execute-api.us-east-1.amazonaws.com/prod/api/photo'

interface UploadResponse {
  photoId: string
  uploadUrl?: string  // Presigned URL for direct S3 upload
}

interface DownloadResponse {
  downloadUrl: string
}

/**
 * Upload a photo to S3 storage
 *
 * Strategy:
 * 1. Try presigned URL upload (more efficient, works on most browsers)
 * 2. Fall back to direct base64 upload if presigned fails (iOS Safari edge cases)
 *
 * @param imageBlob - The photo blob to upload
 * @returns photoId - UUID to retrieve the photo later
 */
export async function uploadPhoto(imageBlob: Blob): Promise<string> {
  console.log('[PhotoStorage] Uploading photo, size:', Math.round(imageBlob.size / 1024), 'KB')

  try {
    // First, get a presigned upload URL
    const presignedResponse = await fetch(`${API_BASE}/upload`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    })

    if (!presignedResponse.ok) {
      throw new Error(`Failed to get upload URL: ${presignedResponse.status}`)
    }

    const { photoId, uploadUrl } = await presignedResponse.json() as UploadResponse

    if (uploadUrl) {
      // Upload directly to S3 using presigned URL
      console.log('[PhotoStorage] Uploading to S3 via presigned URL...')
      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': 'image/jpeg' },
        body: imageBlob,
      })

      if (!uploadResponse.ok) {
        throw new Error(`S3 upload failed: ${uploadResponse.status}`)
      }

      console.log('[PhotoStorage] Upload successful, photoId:', photoId)
      return photoId
    }

    // No presigned URL returned, fall back to direct upload
    return await uploadPhotoDirect(imageBlob)
  } catch (error) {
    console.warn('[PhotoStorage] Presigned upload failed, trying direct upload:', error)
    // Fall back to direct base64 upload
    try {
      return await uploadPhotoDirect(imageBlob)
    } catch (directError) {
      const errorMsg = directError instanceof Error ? directError.message : String(directError)
      logError('PHOTO_UPLOAD', errorMsg, `size=${Math.round(imageBlob.size / 1024)}KB`)
      throw directError
    }
  }
}

/**
 * Direct base64 upload fallback
 * Used when presigned URL approach fails (e.g., CORS issues on some devices)
 */
async function uploadPhotoDirect(imageBlob: Blob): Promise<string> {
  console.log('[PhotoStorage] Using direct base64 upload...')

  // Convert blob to base64
  const base64 = await blobToBase64(imageBlob)

  const response = await fetch(`${API_BASE}/upload-direct`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ image: base64 }),
  })

  if (!response.ok) {
    const errorText = await response.text()
    throw new Error(`Direct upload failed: ${response.status} - ${errorText}`)
  }

  const { photoId } = await response.json() as UploadResponse
  console.log('[PhotoStorage] Direct upload successful, photoId:', photoId)
  return photoId
}

/**
 * Retrieve a photo from S3 storage
 *
 * @param photoId - The UUID returned from uploadPhoto
 * @returns Blob of the photo image
 */
export async function retrievePhoto(photoId: string): Promise<Blob> {
  console.log('[PhotoStorage] Retrieving photo:', photoId)

  try {
    // Get presigned download URL
    const response = await fetch(`${API_BASE}/${photoId}`, {
      method: 'GET',
    })

    if (!response.ok) {
      throw new Error(`Failed to get download URL: ${response.status}`)
    }

    const { downloadUrl } = await response.json() as DownloadResponse

    // Download the photo from S3
    console.log('[PhotoStorage] Downloading from S3...')
    const photoResponse = await fetch(downloadUrl)

    if (!photoResponse.ok) {
      throw new Error(`Photo download failed: ${photoResponse.status}`)
    }

    const blob = await photoResponse.blob()
    console.log('[PhotoStorage] Retrieved photo, size:', Math.round(blob.size / 1024), 'KB')
    return blob
  } catch (error) {
    const errorMsg = error instanceof Error ? error.message : String(error)
    logError('PHOTO_DOWNLOAD', errorMsg, `photoId=${photoId}`)
    throw error
  }
}

/**
 * Convert a photo blob to a File object for API calls
 */
export async function photoToFile(photoId: string, filename = 'photo.jpg'): Promise<File> {
  const blob = await retrievePhoto(photoId)
  return new File([blob], filename, { type: 'image/jpeg' })
}

/**
 * Check if a photo exists in storage
 */
export async function photoExists(photoId: string): Promise<boolean> {
  try {
    const response = await fetch(`${API_BASE}/${photoId}`, {
      method: 'GET',
    })
    return response.ok
  } catch {
    return false
  }
}

// Helper function to convert blob to base64
function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result)
      } else {
        reject(new Error('Failed to convert blob to base64'))
      }
    }
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })
}
